/*
 * The theme is stamped on <html> before the first paint by the inline script in index.html; this is
 * the same decision expressed in TypeScript, for the moment a person changes it. "system" is a real
 * value and not the absence of one: following `prefers-color-scheme` is a choice, and it is the
 * default.
 */

import { useEffect, useState } from "react"
import { readValue, storageKey, writeValue } from "../../lib/storage.ts"

export type Theme = "system" | "light" | "dark"

const THEME_KEY = storageKey("theme")

function isTheme(value: string): value is Theme {
	return value === "system" || value === "light" || value === "dark"
}

/** The stored choice, or "system" when nothing was chosen or storage is refused. */
export function storedTheme(): Theme {
	const value = readValue(THEME_KEY)
	return isTheme(value) ? value : "system"
}

/** What "system" means right now — the same question index.html's pre-paint script asks. */
function systemTheme(): "light" | "dark" {
	return globalThis.matchMedia?.(DARK_QUERY).matches ? "dark" : "light"
}

const DARK_QUERY = "(prefers-color-scheme: dark)"

/**
 * Writes the choice and stamps the theme it resolves to. "system" is resolved here rather than left
 * to the stylesheet: the pre-paint script stamps a resolved value too, and the token layer defines
 * the dark palette under `[data-theme="dark"]` alone — removing the attribute on a dark system
 * turned the running instance white until the next reload.
 */
export function applyTheme(theme: Theme): void {
	writeValue(THEME_KEY, theme === "system" ? "" : theme)
	document.documentElement.dataset.theme = theme === "system" ? systemTheme() : theme
}

/** The theme and a setter for the appearance settings page. */
export function useTheme(): [Theme, (next: Theme) => void] {
	const [theme, setTheme] = useState<Theme>(storedTheme)
	useEffect(() => {
		applyTheme(theme)
		// While the choice is "system", the system may change its mind under a running tab.
		if (theme !== "system") return
		const media = globalThis.matchMedia?.(DARK_QUERY)
		if (!media) return
		const follow = () => applyTheme("system")
		media.addEventListener("change", follow)
		return () => media.removeEventListener("change", follow)
	}, [theme])
	return [theme, setTheme]
}
